import type { FlowRelationEntry } from '../api/getDomainFlowRelations';

export interface FlowAvailableOptions {
  companyIds: number[];
  branchIds: number[];
  domains: string[];
  flows: string[];
}

export interface FlowDisplayItem {
  flow: string;
  domain: string;
}

export class FlowBiMap {
  private byCompany = new Map<number, FlowRelationEntry[]>();
  private byBranch = new Map<number, FlowRelationEntry[]>();
  private byDomain = new Map<string, FlowRelationEntry[]>();
  private byFlow = new Map<string, FlowRelationEntry[]>();
  private readonly allData: FlowRelationEntry[];

  private cachedAll: FlowAvailableOptions | null = null;
  private cache = new Map<string, FlowAvailableOptions>();
  private groupedCache = new Map<string, Map<string, FlowDisplayItem[]>>();

  constructor(data: FlowRelationEntry[]) {
    this.allData = data;

    data.forEach((item) => {
      this.pushTo(this.byCompany, item.company_id, item);
      this.pushTo(this.byBranch, item.branch_id, item);
      this.pushTo(this.byDomain, item.domain, item);
      this.pushTo(this.byFlow, item.flow, item);
    });
  }

  private pushTo<K>(map: Map<K, FlowRelationEntry[]>, key: K, item: FlowRelationEntry): void {
    const existing = map.get(key);
    if (existing) {
      existing.push(item);
    } else {
      map.set(key, [item]);
    }
  }

  private extractUnique(items: FlowRelationEntry[]): FlowAvailableOptions {
    const companyIds = [...new Set(items.map((i) => i.company_id))];
    const branchIds = [...new Set(items.map((i) => i.branch_id))];
    const domains = [...new Set(items.map((i) => i.domain))];
    const flows = [...new Set(items.map((i) => i.flow))];
    return { companyIds, branchIds, domains, flows };
  }

  getAll(): FlowAvailableOptions {
    if (!this.cachedAll) {
      this.cachedAll = this.extractUnique(this.allData);
    }
    return this.cachedAll;
  }

  getForSelection(
    companyIds: number[] = [],
    branchIds: number[] = [],
    domains: string[] = []
  ): FlowAvailableOptions {
    const hasCompanies = companyIds.length > 0;
    const hasBranches = branchIds.length > 0;
    const hasDomains = domains.length > 0;

    if (!hasCompanies && !hasBranches && !hasDomains) return this.getAll();

    const cacheKey = `sel:${[...companyIds].sort().join(',')};${[...branchIds].sort().join(',')};${[...domains].sort().join(',')}`;
    const cached = this.cache.get(cacheKey);
    if (cached) return cached;

    let items: FlowRelationEntry[] = this.allData;

    if (hasCompanies) {
      const companySet = new Set(companyIds);
      items = items.filter((item) => companySet.has(item.company_id));
    }

    if (hasBranches) {
      const branchSet = new Set(branchIds);
      items = items.filter((item) => branchSet.has(item.branch_id));
    }

    if (hasDomains) {
      const domainSet = new Set(domains);
      items = items.filter((item) => domainSet.has(item.domain));
    }

    const result = this.extractUnique(items);
    this.cache.set(cacheKey, result);
    return result;
  }

  getFlowsGroupedByDomain(
    companyIds: number[] = [],
    branchIds: number[] = []
  ): Map<string, FlowDisplayItem[]> {
    const cacheKey = `grp:${[...companyIds].sort().join(',')};${[...branchIds].sort().join(',')}`;
    const cached = this.groupedCache.get(cacheKey);
    if (cached) return cached;

    let items: FlowRelationEntry[] = this.allData;

    if (companyIds.length > 0) {
      const companySet = new Set(companyIds);
      items = items.filter((item) => companySet.has(item.company_id));
    }

    if (branchIds.length > 0) {
      const branchSet = new Set(branchIds);
      items = items.filter((item) => branchSet.has(item.branch_id));
    }

    const grouped = new Map<string, FlowDisplayItem[]>();
    const seen = new Set<string>();

    items.forEach((item) => {
      const key = `${item.domain}:${item.flow}`;
      if (seen.has(key)) return;
      seen.add(key);

      const displayItem: FlowDisplayItem = {
        flow: item.flow,
        domain: item.domain,
      };
      const existing = grouped.get(item.domain);
      if (existing) {
        existing.push(displayItem);
      } else {
        grouped.set(item.domain, [displayItem]);
      }
    });

    this.groupedCache.set(cacheKey, grouped);
    return grouped;
  }
}
